import { DeeplyReadonly, Filter } from '@cubejs-client/core';
import { isAllQuarters } from '@/api/hcp/partnerscore/utils/partnerScoreUtils';

export type DateItem = {
  year: number;
  quarter: number;
};

const createYearFilter = (cubeName: string, year: number): Filter => ({
  member: `${cubeName}.year`,
  operator: 'equals',
  values: [`${year}`],
});

const createQuarterFilter = (cubeName: string, quarter: number | string[]): Filter => ({
  member: `${cubeName}.quarter`,
  operator: 'equals',
  values: Array.isArray(quarter) ? quarter : [`${quarter}`],
});

const createYearQuarterFilter = (cubeName: string, year: number, quarter: number | string[]): Filter[] => [
  createYearFilter(cubeName, year),
  createQuarterFilter(cubeName, quarter),
];

const createAndFilter = (filters: Filter[]): Filter => ({ and: filters });

const createOrFilter = (filters: Filter[]): Filter => ({ or: filters });

const createFilterArray = (filters: Filter[]): DeeplyReadonly<Filter>[] => filters as DeeplyReadonly<Filter>[];

const processAndSortDates = (dateList: DateItem[]): DateItem[] => {
  if (!dateList.length) return [];

  return [...new Set(dateList.map(({ year: currYear, quarter: currQuarter }) => `${currYear}-${currQuarter}`))]
    .map(item => {
      const [y, q] = item.split('-').map(Number);
      return { year: y, quarter: q };
    })
    .sort((a, b) => (b.year !== a.year ? b.year - a.year : b.quarter - a.quarter));
};

const findPreviousPeriod = (
  sortedDates: DateItem[],
  targetYear: number,
  targetQuarter: number,
): DateItem | undefined => {
  return sortedDates.find(({ year: y, quarter: q }) => y < targetYear || (y === targetYear && q < targetQuarter));
};

export const getLastDate = (date: DateItem[]): DateItem => {
  if (date.length > 0) {
    return {
      year: date[date.length - 1].year,
      quarter: date[date.length - 1].quarter,
    };
  }
  return {
    year: 2020,
    quarter: 4,
  };
};

const getQuarter = (q: number): string[] => (isAllQuarters(q) ? ['1', '2', '3', '4'] : [`${q}`]);

export const createFiltersWithPreviousPeriods = (
  cubeName: string,
  year: number,
  quarter: number,
  lastDate: DateItem,
): DeeplyReadonly<Filter>[] => {
  const previousYearQuarter = year === lastDate.year ? lastDate.quarter : 4;

  if (year === lastDate.year) {
    return createFilterArray([
      createYearFilter(cubeName, year),
      {
        member: `${cubeName}.quarter`,
        operator: 'lte',
        values: getQuarter(quarter),
      },
    ]);
  }

  return createFilterArray([
    createOrFilter([
      createAndFilter([
        createYearFilter(cubeName, year),
        {
          member: `${cubeName}.quarter`,
          operator: 'equals',
          values: getQuarter(quarter),
        },
      ]),
      createAndFilter([
        {
          member: `${cubeName}.year`,
          operator: 'equals',
          values: [`${year - 1}`],
        },
        {
          member: `${cubeName}.quarter`,
          operator: 'equals',
          values: [`${previousYearQuarter}`],
        },
      ]),
    ]),
  ]);
};

export const verifyRequestedPeriodHasData = <Data>(dataList: Array<Record<string, Data>>, year: number): boolean => {
  return !!dataList.find(data => data['partnerscore.year'] === year);
};

export const createFiltersWithImmediatePreviousPeriods = (
  cubeName: string,
  year: number,
  quarter: number,
  dateList: DateItem[],
): DeeplyReadonly<Filter>[] => {
  if (!dateList.length) return [];

  const sortedDates = processAndSortDates(dateList);
  const prevPeriod = findPreviousPeriod(sortedDates, year, quarter);

  const filters: Filter[] = [createAndFilter(createYearQuarterFilter(cubeName, year, getQuarter(quarter)))];

  if (prevPeriod) {
    filters.push(
      createAndFilter([
        createYearFilter(cubeName, prevPeriod.year),
        createQuarterFilter(cubeName, isAllQuarters(quarter) ? getQuarter(quarter) : [`${prevPeriod.quarter}`]),
      ]),
    );
  }

  return createFilterArray([createOrFilter(filters)]);
};

export const createFiltersWithCurrentPeriod = (
  cubeName: string,
  year: number,
  quarter: number,
): DeeplyReadonly<Filter>[] => {
  return createFilterArray(createYearQuarterFilter(cubeName, year, getQuarter(quarter)));
};

const addQuartersForYear = (quarters: { year: number; quarter: number }[], year: number): void => {
  quarters.push({ year, quarter: 1 });
  quarters.push({ year, quarter: 2 });
  quarters.push({ year, quarter: 3 });
  quarters.push({ year, quarter: 4 });
};

const addQuartersForYearRange = (
  quarters: { year: number; quarter: number }[],
  startYear: number,
  endYear: number,
): void => {
  for (let year = startYear; year <= endYear; year++) {
    addQuartersForYear(quarters, year);
  }
};

const calculateQuarterWithYearTransition = (
  baseQuarter: number,
  baseYear: number,
  offset: number,
): { year: number; quarter: number } => {
  let quarter = baseQuarter + offset;
  let year = baseYear;

  while (quarter < 1) {
    quarter += 4;
    year--;
  }

  while (quarter > 4) {
    quarter -= 4;
    year++;
  }

  return { year, quarter };
};

const generateQuartersForAllQuartersSelection = (currentYear: number): { year: number; quarter: number }[] => {
  const quarters: { year: number; quarter: number }[] = [];

  addQuartersForYearRange(quarters, currentYear - 2, currentYear + 2);

  return quarters;
};

const generateQuartersForSpecificQuarterSelection = (
  currentYear: number,
  currentQuarter: number,
): { year: number; quarter: number }[] => {
  const quarters: { year: number; quarter: number }[] = [];

  for (let i = 6; i > 0; i--) {
    const { year, quarter } = calculateQuarterWithYearTransition(currentQuarter, currentYear, -i);
    quarters.push({ year, quarter });
  }

  quarters.push({ year: currentYear, quarter: currentQuarter });

  for (let i = 1; i <= 2; i++) {
    const { year, quarter } = calculateQuarterWithYearTransition(currentQuarter, currentYear, i);
    quarters.push({ year, quarter });
  }

  return quarters;
};

const createQuarterFilterFromDate = (
  cubeName: string,
  { year, quarter }: { year: number; quarter: number },
): Filter => {
  return createAndFilter(createYearQuarterFilter(cubeName, year, quarter));
};

export const createFiltersWithExtendedPeriods = ({
  cubeNameArg,
  currentYear,
  currentQuarter,
}: {
  cubeNameArg: string;
  currentYear: number;
  currentQuarter: number;
}): DeeplyReadonly<Filter>[] => {
  const quarters = isAllQuarters(currentQuarter)
    ? generateQuartersForAllQuartersSelection(currentYear)
    : generateQuartersForSpecificQuarterSelection(currentYear, currentQuarter);

  return createFilterArray([
    createOrFilter(quarters.map(quarter => createQuarterFilterFromDate(cubeNameArg, quarter))),
  ]);
};

const isArrayOfStrings = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(arrayItem => typeof arrayItem === 'string');

export function getCountryCodesFromRootAnd(filter: { and?: Filter[] } | undefined): string[] {
  if (!filter?.and) return [];
  const countryClause = filter.and.find(clause => {
    return 'member' in clause && clause.member === 'hcp.country_code';
  });
  if (!countryClause || !('values' in countryClause)) {
    return [];
  }
  const countryCodeValues = countryClause.values;
  if (!isArrayOfStrings(countryCodeValues)) {
    return [];
  }
  return countryCodeValues;
}
